import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { provisionTenant, getTenantInfo } from "../api-services/tenant";
import { useMyInfo } from "./user.hooks";
import { QUERY_KEY } from "../commons/query-key";
import { getCompanyIdCookie } from "../utils/company-cookie";

/**
 * Hook to check tenant status
 * Calls /api/v1/tenants/tenantInfo with agencyId derived from the companyId cookie,
 * falling back to the agency/company ID from user info when the cookie is not set.
 */
export const useTenantStatus = () => {
  const { data: userData } = useMyInfo();
  const originCompanyId =
    (userData as any)?.data?.companyId || (userData as any)?.companyId;
  const agencyID = getCompanyIdCookie() || originCompanyId;
  const {
    data: accountInfo,
    isLoading,
    isError,
  } = useQuery(
    [QUERY_KEY.TENANT_STATUS, agencyID],
    () => getTenantInfo(agencyID),
    {
      enabled: !!agencyID,
      retry: false,
      staleTime: 30000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  );

  let tenantID = null;
  let tenantName = null;
  let customerSupportID = null;

  if (accountInfo?.data) {
    const data = Array.isArray(accountInfo.data)
      ? accountInfo.data[0]
      : accountInfo.data;
    tenantID = data?.tenantId || null;
    tenantName = data?.tenantName || null;
    customerSupportID = data?.customerSupportID || null;
  } else if (accountInfo) {
    tenantID = accountInfo.data || null;
    tenantName = accountInfo.data || null;
    customerSupportID = accountInfo.data || null;
  }

  const hasTenant = !!tenantID;
  const hasAgencyID = !!agencyID;

  return {
    hasTenant,
    tenantID,
    tenantName,
    agencyID,
    customerSupportID,
    hasAgencyID,
    isLoading,
    isError,
    accountInfo,
  };
};

/**
 * Hook to provision a new tenant or map existing tenant to agency
 */
export const useProvisionTenant = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: userData } = useMyInfo();
  const originCompanyId =
    (userData as any)?.data?.companyId || (userData as any)?.companyId;
  const agencyID = getCompanyIdCookie() || originCompanyId;

  return useMutation(
    (payload: { name?: string; adminEmail?: string; tenantID?: string }) =>
      provisionTenant(payload, agencyID),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(QUERY_KEY.TENANT_STATUS);

        const isMapping = variables.tenantID && !variables.name;
        const successMessage = isMapping
          ? "Tenant mapped successfully!"
          : "Workspace initialized successfully!";

        enqueueSnackbar(successMessage, {
          variant: "success",
        });
      },
      onError: (error: any, variables) => {
        const isMapping = variables.tenantID && !variables.name;
        let errorMessage = isMapping
          ? "Failed to map tenant. Please try again."
          : "Failed to initialize workspace";

        if (error?.status === 403) {
          errorMessage =
            "Only Agency Admins can perform this action. Please contact your administrator.";
        } else if (error?.status === 400) {
          errorMessage =
            error?.data?.message || "Invalid input. Please check your data.";
        } else if (error?.status === 404) {
          errorMessage = "Tenant not found. Please try again.";
        } else if (error?.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (error?.message) {
          errorMessage = error.message;
        }

        enqueueSnackbar(errorMessage, {
          variant: "error",
        });
      },
    }
  );
};
